import { supabase } from "@/integrations/supabase/client";
import { EmailTemplate, EmailTemplateVariable } from "@/types/communication.types";

/**
 * Fetch all email templates
 */
export const fetchEmailTemplates = async (
  filters?: {
    category?: string;
    searchTerm?: string;
  }
): Promise<EmailTemplate[]> => {
  let query = supabase
    .from("email_templates")
    .select("*")
    .order("name");

  if (filters?.category) {
    query = query.eq("category", filters.category);
  }
  
  if (filters?.searchTerm) {
    query = query.or(`name.ilike.%${filters.searchTerm}%,content.ilike.%${filters.searchTerm}%`);
  }

  const { data, error } = await query;

  if (error) throw error;
  return data || [];
};

/**
 * Fetch a single email template by ID
 */
export const fetchEmailTemplateById = async (id: string): Promise<EmailTemplate> => {
  const { data, error } = await supabase
    .from("email_templates")
    .select("*")
    .eq("id", id)
    .single();

  if (error) throw error;
  return data;
};

/**
 * Create a new email template
 */
export const createEmailTemplate = async (template: Omit<EmailTemplate, "id">): Promise<EmailTemplate> => {
  const { data, error } = await supabase
    .from("email_templates")
    .insert({
      name: template.name,
      subject: template.subject,
      content: template.content,
      category: template.category,
      is_default: template.is_default || false,
      variables: template.variables || [],
      created_by: template.created_by
    })
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * Update an existing email template
 */
export const updateEmailTemplate = async (
  id: string,
  updates: Partial<EmailTemplate>
): Promise<EmailTemplate> => {
  const { data, error } = await supabase
    .from("email_templates")
    .update(updates)
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * Delete an email template
 */
export const deleteEmailTemplate = async (id: string): Promise<boolean> => {
  const { error } = await supabase
    .from("email_templates")
    .delete()
    .eq("id", id);

  if (error) throw error;
  return true;
};

/**
 * Create a duplicate of an existing template
 */
export const duplicateEmailTemplate = async (
  id: string,
  newName?: string
): Promise<EmailTemplate> => {
  // Fetch the template to duplicate
  const template = await fetchEmailTemplateById(id);
  
  // Create new template with duplicated data
  const { data, error } = await supabase
    .from("email_templates")
    .insert({
      name: newName || `${template.name} (Copy)`,
      subject: template.subject,
      content: template.content,
      category: template.category,
      is_default: false, // Duplicates are never default
      variables: template.variables,
      created_by: template.created_by
    })
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * Set a template as the default for its category
 */
export const setDefaultTemplate = async (id: string, category: string): Promise<boolean> => {
  // First, clear any existing default for this category
  await supabase
    .from("email_templates")
    .update({ is_default: false })
    .eq("category", category)
    .eq("is_default", true);
  
  // Set the new default
  const { error } = await supabase
    .from("email_templates")
    .update({ is_default: true })
    .eq("id", id);

  if (error) throw error;
  return true;
};

/**
 * Get available template variables
 */
export const getAvailableTemplateVariables = (): EmailTemplateVariable[] => {
  return [
    { key: "{{contact.firstName}}", description: "Contact's first name", example: "John" },
    { key: "{{contact.lastName}}", description: "Contact's last name", example: "Smith" },
    { key: "{{contact.email}}", description: "Contact's email address", example: "john.smith@example.com" },
    { key: "{{contact.phone}}", description: "Contact's phone number", example: "555-123-4567" },
    { key: "{{organization.name}}", description: "Organization name", example: "Acme Corp" },
    { key: "{{organization.industry}}", description: "Organization industry", example: "Technology" },
    { key: "{{user.firstName}}", description: "Sender's first name", example: "Jane" },
    { key: "{{user.lastName}}", description: "Sender's last name", example: "Doe" },
    { key: "{{user.email}}", description: "Sender's email address", example: "jane.doe@company.com" },
    { key: "{{user.title}}", description: "Sender's job title", example: "Account Manager" },
    { key: "{{date}}", description: "Current date", example: "May 15, 2025" },
    { key: "{{opportunity.name}}", description: "Opportunity name", example: "2025 Contract Renewal" },
    { key: "{{opportunity.value}}", description: "Opportunity value", example: "$15,000" },
  ];
};

/**
 * Process template with variables
 */
export const processTemplate = (
  template: EmailTemplate,
  variables: Record<string, any>
): { subject: string; content: string } => {
  let subject = template.subject;
  let content = template.content;

  // Replace variables in subject and content
  Object.entries(variables).forEach(([key, value]) => {
    const regex = new RegExp(`{{${key}}}`, 'g');
    subject = subject.replace(regex, value);
    content = content.replace(regex, value);
  });
  
  return { subject, content };
};

/**
 * Get template categories
 */
export const getTemplateCategories = async (): Promise<string[]> => {
  const { data, error } = await supabase
    .from("email_templates")
    .select("category")
    .order("category");

  if (error) throw error;
  
  // Extract unique categories
  const categories = [...new Set(data.map(template => template.category))];
  return categories;
};

/**
 * Get default template categories
 */
export const getDefaultTemplateCategories = (): string[] => {
  return [
    "Welcome",
    "Follow-up",
    "Meeting Request",
    "Meeting Confirmation",
    "Thank You",
    "Newsletter",
    "Product Update",
    "Proposal",
    "Contract",
    "Invoice",
    "Holiday",
    "Event Invitation",
    "Custom"
  ];
};